import styled, { css, CSSProperties } from 'styled-components'

import { COLORS2, COLORS, SIZES, BORDER_RADIUS } from '~/common/globalStyles'

import {
  styles,
  InputElementsProps,
} from '../../../shared/components/inputElements'

interface SelectWrapperProps {
  minHeight?: string
}

export const SelectComponentWrapper = styled.div<SelectWrapperProps>`
  position: relative;
  ${({ minHeight }) =>
    minHeight
      ? css`
          min-height: ${minHeight};
        `
      : 'min-height: 32px;'}
`

interface PlaceholderProps {
  color?: string
}

export const Placeholder = styled.div<PlaceholderProps>(
  ({ color }) => `
  min-height: 15px;
  pointer-events: none;
  font-size: 14px;
  ${color && `color: ${color};`}
`,
)

interface OptionRowProps {
  isActive?: boolean
  isSelected?: boolean
  disabled?: boolean
  isCheckbox?: boolean
}

export const OptionRow = styled.div<OptionRowProps>`
  padding: ${SIZES.xs} ${SIZES.small};
  cursor: pointer;

  ${({ isCheckbox }) =>
    !isCheckbox &&
    css`
      &:not(:first-child) {
        border-top: 1px solid #eef1f6;
      }
    `}

  ${({ isActive, theme }) =>
    isActive &&
    css`
      background-color: ${theme.palette.action.hover};
    `};

  ${({ isSelected, isCheckbox }) =>
    isSelected &&
    !isCheckbox &&
    css`
      background-color: #f2f3f8;
    `};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      background-color: #ffffff;
      color: ${COLORS.lightGray};
    `};
`

interface SelectDropdownButtonProps extends InputElementsProps {
  noPadding?: boolean
  borderless?: boolean
  focused?: boolean
  disabled?: boolean
  style?: CSSProperties
}

export const SelectDropdownButton = styled.div<SelectDropdownButtonProps>`
  ${styles.baseInput};
  background: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAaCAYAAACHD21cAAAABGdBTUEAALGPC/xhBQAAAf5JREFUOBHtVL1rU1EUv/dcTItIJwch0r8gQwfXmrzF2jYWXAKFbhVEROwbdGjzcXgvdehg2kFKB2cxS4em0C6at4pCwUwODkLAWRw0+O7t77zktWkiriJ4Ifec+/u4796Xc55WvxnNZtN0Ot2GULlc1i+VSvGoTI8CzC+uxPHPV8CLA65lzMQy88Pvw9oLxnp9N9vr/Wg552YuiLQ+yWQmi+Xyg26KnxmZGzNx7FogskJqrVkiNkki0q4xusjsnwhuZGLeLlqrDpFelbVSVAtDP4ii4yifn3ewewCnsN2K581/bLePPulqtfHIOYUX4ZJNYKqE4Vo98Q+mSmW7rJQN+0sda618wlGepCYcrzxqErFgwvWNzoiHjFH3AH4GuBEE/mafHJ8H3IZotabVccV/5F98A5p5Z8Fau4fDv8SfzH+6BMpT+FUium9mZ2+9xeI6fgXPmyMUdhv52IApQKlVQUwhFgjlE6Jof4kSHVKt1Z4PivncKxjEFUFEKx4TRUcf0DrvACwBn0Cn3CwUbl/Ck9+IEE+qA0sKHHX6Dd1zNwjWXg818k4Odz3EztNiwD2eSQS2LhGmL8AWmR93krVM6cD35pq1vQOYb6SYRJjeE2Xu4LvzNcUpTSQKQXQ5j2Pvp7jkgg2bhDs7aiqUyOzI2saW5ET+U2ZtJf+74xRlgcKObZd2ywAAAABJRU5ErkJggg==)
    right 8px center no-repeat;
  background-size: 7px 13px;
  ${({ style }) =>
    style?.padding
      ? css`
          padding: ${style.padding};
        `
      : css`
          padding-right: 22px;
          padding-top: ${SIZES.xs};
          padding-bottom: ${SIZES.xs};
        `}
  min-height: 32px;
  display: flex;
  cursor: pointer;
  border-color: ${COLORS2.gray[50]};
  box-shadow:
    0 1px 3px 0 rgba(89, 105, 129, 0.05),
    0 1px 1px 0 rgba(0, 0, 0, 0.025);

  &:active {
    ${styles.focus};
  }

  ${({ noPadding }) =>
    noPadding
      ? css`
          padding: 0;
        `
      : ''};
  ${({ focused }) => focused && styles.focus};
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: #e3e7f1;

      &,
      ${Placeholder} {
        color: #96a3b6;
      }
    `};

  & > ${OptionRow} {
    padding: 0;
  }

  ${({ style }) =>
    style?.maxHeight
      ? css`
          max-height: ${style.maxHeight};
        `
      : ''}

  ${({ borderless }) =>
    borderless
      ? css`
          border: none;
          box-shadow: none;
          outline: 0;
          background: transparent;
        `
      : ''};
`

export const Menu = styled.div<{
  menuPosition: 'top'
  menuStyle?: CSSProperties
}>`
  position: absolute;

  margin-bottom: ${SIZES.xxxs};
  width: 100%;
  max-height: ${({ menuStyle }) =>
    menuStyle?.maxHeight ? menuStyle?.maxHeight : '200px'};
  overflow-y: auto;
  overflow-x: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  z-index: 9001;
  background-color: ${({ theme }) => theme.palette.background.paper};
  ${({ menuPosition }) =>
    menuPosition === 'top'
      ? css`
          bottom: 100%;
        `
      : css`
          top: 100%;
        `};

  ${({ menuStyle }) =>
    menuStyle?.marginTop
      ? css`
          margin-top: ${menuStyle.margin};
        `
      : css`
          margin-top: ${SIZES.xxxs};
        `};

  ${({ menuStyle }) =>
    menuStyle?.boxShadow
      ? css`
          box-shadow: ${menuStyle.boxShadow};
        `
      : css`
          box-shadow:
            0 3px 20px rgba(89, 105, 129, 0.3),
            0 1px 2px rgba(0, 0, 0, 0.05),
            0 0 0 1px rgba(89, 105, 129, 0.1);
        `}

  ${({ menuStyle }) =>
    menuStyle?.borderRadius
      ? css`
          border-radius: ${menuStyle.borderRadius};
        `
      : css`
          border-radius: ${BORDER_RADIUS}px;
        `}

  ${({ menuStyle }) =>
    menuStyle?.padding
      ? css`
          padding: ${menuStyle.padding};
        `
      : css`
          padding: 3px 0px;
        `}

  ${({ menuStyle }) =>
    menuStyle?.borderTop &&
    css`
      border-top: ${menuStyle.borderTop};
    `}
`
